import { RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';
import { GoGear } from 'react-icons/go';
import {
  Flex,
  Button,
  SegmentedControl,
  Collapse,
  Text,
  Box,
  Group,
  Stack,
  Grid,
  ActionIcon,
  Switch,
} from '@mantine/core';
import FilterBar from '@worker/components/MyJobs/FilterBar/FilterBar';
import { ViewsTypesEnum } from '@worker/types/myJobs.type';
import { useWorkerStore } from '@worker/stores';

const useActiveFiltersCount = () => {
  const { searchFilters, advancedFilters } = useWorkerStore();
  let count = 0;

  // Search filters
  if (searchFilters.address) count += 1;

  // Advanced filters
  if (
    advancedFilters.jobLocation?.country ||
    advancedFilters.jobLocation?.includeNational === false
  ) {
    count += 1;
  }
  if (advancedFilters.jobTitles?.length > 0) count += 1;
  if (
    (advancedFilters.minHourlyEnabled && advancedFilters.minHourlyRate) ||
    (advancedFilters.minDailyEnabled && advancedFilters.minDailyRate)
  ) {
    count += 1;
  }
  if (advancedFilters.startDate || advancedFilters.endDate) count += 1;

  return count;
};

interface HeaderMobileProps {
  value: ViewsTypesEnum;
  onSearch: ({
    companyName,
    companyId,
    address,
    miles,
  }: {
    companyName: string;
    companyId: string | null;
    address: google.maps.places.PlaceResult | null;
    miles: number | null;
  }) => void;
  onClear: () => void;

  onOpenAdvancedFilters: () => void;
  handleConfigure: () => void;
  handleChangeView: (value: ViewsTypesEnum) => void;
  handleGetInNetwork: () => void;
}

function HeaderMobile({
  value,
  onClear,
  onSearch,
  onOpenAdvancedFilters,
  handleChangeView,
  handleConfigure,
  handleGetInNetwork,
}: HeaderMobileProps) {
  const { showMobileFilters, setShowMobileFilters, isInNetwork } =
    useWorkerStore();
  const activeFiltersCount = useActiveFiltersCount();

  return (
    <Stack>
      <Grid align="center">
        <Grid.Col span={6}>
          <SegmentedControl
            data={[
              { label: 'All Jobs', value: ViewsTypesEnum.ALL_PUBLIC_JOBS },
              { label: 'My Jobs', value: ViewsTypesEnum.MY_JOBS },
            ]}
            value={value}
            onChange={(view) => handleChangeView(view as ViewsTypesEnum)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Flex align="center" justify="flex-end" gap={15}>
            <Switch
              checked={isInNetwork()}
              onChange={handleGetInNetwork}
              color={isInNetwork() ? 'green' : 'red'}
              size="sm"
              aria-label={isInNetwork() ? 'In-Network' : 'Not In-Network'}
              styles={{
                track: {
                  cursor: 'pointer',
                  backgroundColor: isInNetwork()
                    ? undefined
                    : 'var(--mantine-color-red-6)',
                },
              }}
            />
            <ActionIcon
              data-testid="configure-button"
              variant="filled"
              color="var(--light-gray)"
              size="md"
              onClick={handleConfigure}
            >
              <GoGear size={18} color="var(--black)" />
            </ActionIcon>
          </Flex>
        </Grid.Col>
      </Grid>

      <Flex gap="md" mb={10} align="center">
        <Box w="100%" mx={0} bd="1px solid black" bdrs={5}>
          <Group justify="center">
            <Button
              data-testid="search-and-filters-btn"
              variant="transparent"
              c="var(--black)"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              rightSection={
                showMobileFilters ? <RiArrowUpSLine /> : <RiArrowDownSLine />
              }
            >
              <Text size="lg" tt="uppercase" fw={600}>
                Search and Filters
                {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
              </Text>
            </Button>
          </Group>

          <Collapse in={showMobileFilters}>
            <FilterBar
              onSearch={onSearch}
              onClear={onClear}
              onOpenAdvancedFilters={onOpenAdvancedFilters}
            />
          </Collapse>
        </Box>
      </Flex>
    </Stack>
  );
}

export default HeaderMobile;
